import { Component, signal } from '@angular/core';
import { DatePipe, NgForOf, NgIf } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { DividerModule } from 'primeng/divider';
import { SelectButtonModule } from 'primeng/selectbutton';
import { InputNumberModule } from 'primeng/inputnumber';
import { InputSwitchModule } from 'primeng/inputswitch';
import { MessagesModule } from 'primeng/messages';
import { MultiSelectModule } from 'primeng/multiselect';
import { CalendarModule } from 'primeng/calendar';
import { RadioButtonModule } from 'primeng/radiobutton';
import { ActivatedRoute } from '@angular/router';

import { SMSCertifiedNode } from '@modules/campaigns/Domain/Nodes/SMSCertifiedNode';
import { SMSCertifiedConfig } from '@modules/campaigns/Domain/Interfaces/INodeConfig';
import { WorkflowStateService } from '@shared/state/workflow-state.service';
import { NodeType } from '@modules/campaigns/Domain/Enums/NodeType';
import { CopiesService } from '@modules/copies/service/copies.service';
import { CopyModel } from '@models/copies/copies.model';
import { SendMessageInspectorBase } from '../send-message-inspector.base';

@Component({
  selector: 'app-sms-certified-inspector',
  standalone: true,
  imports: [
    NgIf,
    NgForOf,
    FormsModule,
    TranslateModule,
    DividerModule,
    SelectButtonModule,
    InputNumberModule,
    InputSwitchModule,
    MessagesModule,
    MultiSelectModule,
    CalendarModule,
    RadioButtonModule,
    DatePipe,
  ],
  templateUrl: './sms-certified-inspector.component.html',
  styleUrls: ['./sms-certified-inspector.component.scss'],
})
export class SmsCertifiedInspectorComponent extends SendMessageInspectorBase<
  SMSCertifiedNode,
  SMSCertifiedConfig
> {
  protected override get channelType() {
    return 'certified_sms';
  }
  protected override get i18nPrefix() {
    return 'DATA.MODULES.INSPECTORS.SMS_CERTIFIED';
  }

  readonly selectedTemplateIds = signal<string[]>([]);

  override get copyIds(): string[] {
    return this.selectedTemplateIds();
  }

  protected override setCopyIds(ids: string[]) {
    this.selectedTemplateIds.set(ids);
  }

  protected override refreshCopyFromConfig(config: SMSCertifiedConfig) {
    this.selectedTemplateIds.set(config.copyIds ?? []);
  }

  override get nodeValue(): SMSCertifiedNode | null {
    const n = this.node();
    return n?.type === NodeType.SendCertifiedSMS ? (n as SMSCertifiedNode) : null;
  }

  protected override buildConfig(node: SMSCertifiedNode, copyIds: string[]): SMSCertifiedConfig {
    return {
      ...(node.config as SMSCertifiedConfig),
      copyIds,
    } as SMSCertifiedConfig;
  }

  protected override extractPreviewMessage(template: CopyModel | null): string {
    if (!template) return '';
    const block = template.blocks?.find(
      (b) =>
        b.type === 'certifiedSmsText' ||
        b.alias === 'certifiedSmsText' ||
        b.type === 'smsText' ||
        b.alias === 'smsText' ||
        b.type === 'text',
    );
    return block?.translations?.[0]?.text ?? '';
  }

  onTemplatesChange(ids: string[]) {
    this.selectedTemplateIds.set(ids);
    this.scheduleApplyChanges();
  }

  constructor(
    workflow: WorkflowStateService,
    translate: TranslateService,
    copiesService: CopiesService,
    route: ActivatedRoute,
  ) {
    super(workflow, translate, copiesService, route);
  }
}
